import React, { type Dispatch, type SetStateAction, useState } from 'react'
import { SupportedLocale } from '@layerfi/components'
import { useNavigate } from 'react-router-dom'

import './demoControls.scss'

import { useAuth, useLogOut } from '../../contexts/AuthContext'
import { DemoControlsButton } from './DemoControlsButton'
import { DemoControlsResetDemoButton } from './DemoControlsResetDemoButton'
import { DemoControlsToggle } from './DemoControlsToggle'
import { useDemoControls } from './useDemoControls'

interface DemoControlsProps {
  demoName: string
  setImportingTransactions: Dispatch<SetStateAction<boolean>>
  isMobileView: boolean
  setIsMobileView: (value: boolean) => void
  isBookkeepingMode: boolean
  setBookkeepingMode: (value: boolean) => void
  showTooltips: boolean
  setShowTooltips: Dispatch<SetStateAction<boolean>>
  locale: SupportedLocale
  setLocale: Dispatch<SetStateAction<SupportedLocale>>
  useSolopreneurLayout: boolean
  enableTimeTracking: boolean
}

export const DemoControls = ({
  demoName,
  setImportingTransactions,
  isMobileView,
  setIsMobileView,
  isBookkeepingMode,
  setBookkeepingMode,
  showTooltips,
  setShowTooltips,
  locale,
  setLocale,
  useSolopreneurLayout,
  enableTimeTracking,
}: DemoControlsProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const navigate = useNavigate()
  const auth = useAuth()
  const { toggleSolopreneurLayout, toggleEnableTimeTracking } = useDemoControls(demoName)
  const isAdmin = auth.isAdmin

  const handleLogOut = useLogOut()

  const handleDemoSettings = () => {
    navigate(`/${demoName}/demo-settings`)
  }

  return (
    <div className='sidebar-dev-menu'>
      <button data-id='demo-controls-toggle-top' data-expanded={isExpanded} className='demo-controls-toggle' onClick={() => setIsExpanded(!isExpanded)} >
        Demo controls
      </button>
      {isExpanded && (
        <>
        {isAdmin && (
        <section className='demo-management'>
          <h3>Demo management</h3>
          <DemoControlsResetDemoButton demoName={demoName} />
          <DemoControlsButton label='Import data' onClick={() => setImportingTransactions(true)}/>
          <DemoControlsButton label='Demo settings' onClick={handleDemoSettings}/>
          <DemoControlsButton label='Return to admin' onClick={() => navigate('/admin')} />

          <h3>Feature flags</h3>
            <DemoControlsToggle
              active={isBookkeepingMode}
              activeLabel='Set bookkeeping off'
              inactiveLabel='Set bookkeeping on'
              onToggle={() => setBookkeepingMode(!isBookkeepingMode)}
            />
          
            <DemoControlsToggle
              active={showTooltips}
              activeLabel='Hide tooltips'
              inactiveLabel='Show tooltips'
              onToggle={() => setShowTooltips(!showTooltips)}
            />
          
            <DemoControlsToggle
              active={useSolopreneurLayout}
              activeLabel='Use accounting layout'
              inactiveLabel='Use solopreneur layout'
              onToggle={() => toggleSolopreneurLayout(useSolopreneurLayout)}
            />

            <DemoControlsToggle
              active={enableTimeTracking}
              activeLabel='Disable time tracking'
              inactiveLabel='Enable time tracking'
              onToggle={() => toggleEnableTimeTracking(enableTimeTracking)}
            />
        </section>
        )}
        <section className='demo-management'>
          <h3>{isAdmin ? 'Customer-enabled flags' : 'Feature flags'}</h3>
            <DemoControlsToggle
              active={isMobileView}
              activeLabel='Set to desktop view'
              inactiveLabel='Set to mobile view'
              onToggle={() => setIsMobileView(!isMobileView)}
            />
            <DemoControlsToggle
              active={locale === SupportedLocale.enUS}
              activeLabel='Switch to fr-CA'
              inactiveLabel='Switch to en-US'
              onToggle={() =>
                setLocale(locale === SupportedLocale.enUS
                  ? SupportedLocale.frCA
                  : SupportedLocale.enUS)}
            />
            <DemoControlsButton label='Log out' onClick={handleLogOut} />
          </section>
          <button data-id='demo-controls-toggle-bottom' className='demo-controls-toggle' onClick={() => setIsExpanded(!isExpanded)} >
            Demo controls
          </button>
        </>
      )}
    </div>
  )
}
